import BreakpointHelper from '../../00_utilities/helpers/BreakpointHelper';

class Navigation {
    constructor(elem) {
        this.elem = elem;
        this.breakpoint = '65em';
        this.mainToggle = document.getElementById('main-menu-toggle');
        this.layoutDesktop = BreakpointHelper.aboveBreakpoint(this.breakpoint);
        this.boundItemMouseEnterListener = this.openNavigation.bind(this);
        this.boundItemMouseLeaveListener = this.closeNavigation.bind(this);
        this.boundItemFocusInListener = this.handleFocusBehaviour.bind(this);
        this.focusableNavigationItems = this.elem.querySelectorAll('.navigation__menu-toggle-input, a');
        this.navigationHoverItems = this.elem.querySelectorAll('.navigation__open-on-hover');
    }

    init() {
        this.setAccessibilityLabels();
        this.initToggleChangeListener();
        this.behaviourOnMainToggleChange();
        const aboveBreakpoint = BreakpointHelper.aboveBreakpoint(this.breakpoint);
        this.initResizeSensibleFunctions(aboveBreakpoint);

        if (aboveBreakpoint) {
            this.setSubmenuOffset(aboveBreakpoint);
        }
    }

    /**
     * set accessibility labels on main toggle
     */
    setAccessibilityLabels() {
        if (this.layoutDesktop) {
            this.mainToggle.removeAttribute('role');

            return;
        }
        if (!this.mainToggle.hasAttribute('role')) {
            this.mainToggle.setAttribute('role', 'button');
        }
    }

    initResizeSensibleFunctions(aboveBreakpoint) {
        this.initLayouts();

        const resizeObserver = new ResizeObserver(() => {
            const breakpointInfo = BreakpointHelper.breakpointCrossed(this.breakpoint, aboveBreakpoint);
            aboveBreakpoint = breakpointInfo.aboveBreakpoint;
            const { breakpointCrossed } = breakpointInfo;

            this.setSubmenuOffset(aboveBreakpoint);

            if (breakpointCrossed) {
                this.layoutDesktop = aboveBreakpoint;
                Navigation.allowScrolling();
                Navigation.closeNavigation();
                this.initLayouts();
            }
        });
        resizeObserver.observe(this.elem);
    }

    initLayouts() {
        if (this.layoutDesktop) {
            this.initOpenOnHover();
            this.initFocusIn();
            return;
        }
        this.closeOnHover();
        this.removeFocusIn();
    }

    /**
     * add spacing to the left so the submenu is positioned right
     * below its main menu item
     * @param aboveBreakpoint
     */
    setSubmenuOffset(aboveBreakpoint = true) {
        const navigationItems = this.elem.querySelectorAll('.navigation__item.navigation__open-on-hover');

        navigationItems.forEach((item) => {
            const navigationSubWrapper = item.querySelector('.navigation__sub-wrapper');
            if (navigationSubWrapper) {
                let spacingLeft = 0;
                if (aboveBreakpoint) {
                    spacingLeft = item.offsetLeft;
                }
                navigationSubWrapper.style.setProperty('--navigation-sub-padding', `${spacingLeft}px`);
            }
        });
    }

    /**
     * prevent or allow scrolling on body in case
     * mobile navigation is open
     */
    behaviourOnMainToggleChange() {
        this.mainToggle.addEventListener('change', () => {
            if (this.mainToggle.checked) {
                Navigation.preventScrolling();
                document.addEventListener('click', this.bodyClickListener);
                return;
            }

            Navigation.allowScrolling();
            document.removeEventListener('click', this.bodyClickListener);
        });
    }

    static preventScrolling(element = 'body') {
        window.requestAnimationFrame(() => {
            document.querySelector(element).classList.add('no-scroll');
        });
    }

    static allowScrolling(element = 'body') {
        window.requestAnimationFrame(() => {
            document.querySelector(element).classList.remove('no-scroll');
        });
    }

    /**
     * init change listener on every .navigation__menu-toggle-input
     */
    initToggleChangeListener() {
        const toggleInputs = this.elem.querySelectorAll('.navigation__menu-toggle-input');
        toggleInputs.forEach((toggle) => {
            toggle.addEventListener('change', (event) => {
                this.closeSiblings(event);
                this.checkMainToggle(event);
            });
        });
    }

    /**
     * check main toggle checkbox for correct styling
     */
    checkMainToggle(event) {
        if (this.layoutDesktop) {
            const { checked } = event.currentTarget;
            Navigation.checkboxHelper(this.mainToggle, checked, true);
        }
    }

    closeSiblings(event) {
        const currentMenu = event.currentTarget.closest('.navigation__open-on-hover');
        const menuSiblings = this.getSiblings(currentMenu);

        menuSiblings.forEach((sibling) => {
            const siblingsToggle = sibling.querySelector('.navigation__menu-toggle-input');
            if (siblingsToggle) {
                Navigation.checkboxHelper(siblingsToggle, false);
            }
        });
    }

    openNavigation(event) {
        this.closeSiblings(event);

        Navigation.checkboxHelper(this.mainToggle, true);
        Navigation.checkboxHelper(event.currentTarget.querySelector(':scope > .navigation__menu-toggle-input'), true);

        const toggles = event.currentTarget.querySelectorAll('.navigation__menu-toggle');
        for (const toggle of toggles) {
            toggle.style.pointerEvents = 'none';
        }
    }

    closeNavigation(event) {
        if (!event.currentTarget.closest('.navigation__sub')) {
            Navigation.checkboxHelper(this.mainToggle, false);
        }
        Navigation.checkboxHelper(event.currentTarget.querySelector(':scope > .navigation__menu-toggle-input'), false);

        const toggles = event.currentTarget.querySelectorAll('.navigation__menu-toggle');
        for (const toggle of toggles) {
            toggle.style.pointerEvents = 'auto';
        }
    }

    initOpenOnHover() {
        if (this.navigationHoverItems) {
            for (const item of this.navigationHoverItems) {
                item.addEventListener('mouseenter', this.boundItemMouseEnterListener);
                item.addEventListener('mouseleave', this.boundItemMouseLeaveListener);
            }
        }
    }

    closeOnHover() {
        if (this.navigationHoverItems) {
            for (const item of this.navigationHoverItems) {
                item.removeEventListener('mouseenter', this.boundItemMouseEnterListener);
                item.removeEventListener('mouseleave', this.boundItemMouseLeaveListener);
            }
        }
    }

    static checkboxHelper(checkbox, check, triggerChange) {
        if (typeof checkbox.prop === 'undefined') {
            checkbox.checked = check;

            if (triggerChange) {
                const event = new Event('trigger');
                checkbox.dispatchEvent(event);
            }
            return;
        }

        checkbox
            .prop('checked', check)
            .attr('checked', check);

        if (triggerChange) {
            const event = new Event('change', { bubbles: true, cancelable: true });
            checkbox.dispatchEvent(event);
        }
    }

    getSiblings(element) {
        return Array.prototype.filter.call(element.parentNode.children, (sibling) => {
            return sibling !== element;
        });
    }

    static closeNavigation() {
        Navigation.checkboxHelper(document.getElementById('main-menu-toggle'), false, true);
        document.querySelectorAll('.navigation__menu-toggle-input').forEach((toggle) => {
            Navigation.checkboxHelper(toggle, false);
        });
    }

    handleFocusBehaviour(event) {
        const { currentTarget } = event;
        if (currentTarget.parentNode.classList.contains('navigation__item')) {
            Navigation.closeNavigation();
            return;
        }

        const subItem = currentTarget.closest('.navigation__sub-item');
        if (subItem) {
            const siblings = this.getSiblings(subItem);

            siblings.forEach((item) => {
                const toggles = item.querySelectorAll('input');
                toggles.forEach((toggle) => {
                    Navigation.checkboxHelper(toggle, false);
                });
            });
        }
    }

    initFocusIn() {
        if (this.focusableNavigationItems) {
            for (const item of this.focusableNavigationItems) {
                item.addEventListener('focusin', this.boundItemFocusInListener);
            }
        }
    }

    removeFocusIn() {
        if (this.focusableNavigationItems) {
            for (const item of this.focusableNavigationItems) {
                item.removeEventListener('focusin', this.boundItemFocusInListener);
            }
        }
    }

    initFocusHandling() {
        if (this.focusableNavigationItems) {
            for (const item of this.focusableNavigationItems) {
                item.removeEventListener('focusin', this.boundItemFocusInListener);

                if (this.layoutDesktop) {
                    item.addEventListener('focusin', this.boundItemFocusInListener);
                }
            }
        }
    }

    bodyClickListener(e) {
        const { target } = e;
        const isNavigation = target.closest('.navigation') !== null
            || document.querySelector('.navigation') === target
            || target.closest('.navigation-control__trigger') !== null
            || document.querySelector('.navigation-control__trigger') === target
            || document.querySelector('.menu-toggle-input') === target;

        if (!isNavigation) {
            Navigation.closeNavigation();
            Navigation.allowScrolling();
        }
    }
}

export default Navigation;
